import { ONLINE_API_ORIGIN } from './contract.js';
import { OnlineApiError, redactOnlineSecrets } from './errors.js';

export type OnlineQueryValue = string | number | boolean | undefined;

export interface OnlineRequestOptions {
  query?: Record<string, OnlineQueryValue>;
  headers?: Record<string, string>;
  authenticate?: boolean;
  responseType?: 'json' | 'binary';
}

export interface OnlineResponse<T = unknown> {
  status: number;
  data?: T;
  bytes?: Uint8Array;
  etag?: string;
  pollInterval?: number;
  sha256?: string;
}

export interface OnlineClientOptions {
  apiKey?: string;
  timeoutMs?: number;
  userAgent?: string;
  fetch?: typeof fetch;
}

const MAX_ERROR_BODY_LENGTH = 2048;

export class OnlineClient {
  private readonly apiKey?: string;
  private readonly timeoutMs: number;
  private readonly userAgent: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: OnlineClientOptions = {}) {
    this.apiKey = options.apiKey;
    this.timeoutMs = options.timeoutMs ?? 30_000;
    this.userAgent = options.userAgent ?? '@zaparoo/cli/development';
    this.fetchImpl = options.fetch ?? globalThis.fetch;
  }

  async get<T = unknown>(
    path: string,
    options: OnlineRequestOptions = {},
  ): Promise<OnlineResponse<T>> {
    const url = buildOnlineUrl(path, options.query);
    const authenticate = options.authenticate !== false;
    if (authenticate && !this.apiKey) {
      throw new OnlineApiError(
        'Online User API key is not configured. Use online auth set or ZAPAROO_ONLINE_USER_API_KEY.',
        'authentication',
      );
    }

    const headers = new Headers({
      Accept: options.responseType === 'binary' ? 'application/octet-stream' : 'application/json',
      'User-Agent': this.userAgent,
      ...options.headers,
    });
    if (authenticate && this.apiKey) headers.set('Authorization', `Bearer ${this.apiKey}`);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetchImpl(url, {
        method: 'GET',
        headers,
        redirect: 'error',
        signal: controller.signal,
      });
      const metadata = responseMetadata(response);
      if (response.status === 304) return { status: 304, ...metadata };
      if (response.status >= 300 && response.status < 400) {
        throw new OnlineApiError('Online User API redirect refused', 'redirect', {
          status: response.status,
        });
      }
      if (!response.ok) throw await responseError(response);

      if (options.responseType === 'binary') {
        return {
          status: response.status,
          bytes: new Uint8Array(await response.arrayBuffer()),
          ...metadata,
        };
      }

      if (response.status === 204) return { status: response.status, ...metadata };
      const text = await response.text();
      if (!text) return { status: response.status, ...metadata };
      try {
        return { status: response.status, data: JSON.parse(text) as T, ...metadata };
      } catch {
        throw new OnlineApiError('Online User API returned invalid JSON', 'invalid-response', {
          status: response.status,
        });
      }
    } catch (error) {
      if (error instanceof OnlineApiError) throw error;
      if (controller.signal.aborted) {
        throw new OnlineApiError('Online User API request timed out', 'timeout');
      }
      const message = errorMessage(error);
      if (/redirect/i.test(message)) {
        throw new OnlineApiError('Online User API redirect refused', 'redirect');
      }
      throw new OnlineApiError(`Online User API request failed: ${message}`, 'network');
    } finally {
      clearTimeout(timer);
    }
  }
}

export function buildOnlineUrl(path: string, query: Record<string, OnlineQueryValue> = {}): URL {
  let url: URL;
  try {
    url = new URL(path, ONLINE_API_ORIGIN);
  } catch {
    throw new OnlineApiError('Invalid Online User API path', 'invalid-request');
  }
  if (
    !path.startsWith('/') ||
    url.origin !== ONLINE_API_ORIGIN ||
    url.username ||
    url.password ||
    (url.pathname !== '/v1' && !url.pathname.startsWith('/v1/'))
  ) {
    throw new OnlineApiError('Online requests must use an official /v1 path', 'invalid-request');
  }
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined) url.searchParams.set(name, String(value));
  }
  return url;
}

async function responseError(response: Response): Promise<OnlineApiError> {
  const body = redactOnlineSecrets((await response.text()).slice(0, MAX_ERROR_BODY_LENGTH));
  const retryAfter = positiveInteger(response.headers.get('retry-after'));
  const rateLimitScope = response.headers.get('x-ratelimit-scope') ?? undefined;
  const details = { status: response.status, retryAfter, rateLimitScope };

  if (response.status === 401) {
    return new OnlineApiError(
      'Online User API key is missing, invalid, or revoked',
      'authentication',
      {
        status: response.status,
      },
    );
  }
  if (response.status === 403) {
    return new OnlineApiError('Online User API key lacks the required scope', 'authorization', {
      status: response.status,
    });
  }
  if (response.status === 404) {
    return new OnlineApiError('Online User API resource was not found', 'not-found', {
      status: response.status,
    });
  }
  if (response.status === 429) {
    const backupEgress =
      rateLimitScope === 'backup-egress' || /daily egress cap reached/i.test(body);
    return new OnlineApiError(
      backupEgress
        ? 'Online User API daily backup egress cap reached'
        : 'Online User API request rate limit exceeded',
      backupEgress ? 'backup-egress' : 'rate-limit',
      details,
    );
  }
  if (response.status >= 500) {
    return new OnlineApiError('Online User API server error', 'server', details);
  }
  const suffix = body ? `: ${body}` : '';
  return new OnlineApiError(
    `Online User API request failed with status ${response.status}${suffix}`,
    'invalid-response',
    details,
  );
}

function responseMetadata(response: Response): Omit<OnlineResponse, 'status' | 'data' | 'bytes'> {
  return {
    etag: response.headers.get('etag') ?? undefined,
    pollInterval: positiveInteger(response.headers.get('x-poll-interval')),
    sha256: response.headers.get('x-zaparoo-object-sha256') ?? undefined,
  };
}

function positiveInteger(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
}

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return redactOnlineSecrets(String(error));
  const cause = error.cause instanceof Error ? `: ${error.cause.message}` : '';
  return redactOnlineSecrets(`${error.message}${cause}`);
}
